import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api/client.js";

interface Influencer { id: string; name: string; }
interface StructuredBrief {
  contentType?: string;
  hashtags?: string[];
  callToAction?: string;
  targetAudience?: string;
  [key: string]: unknown;
}
interface Idea {
  id: string;
  title: string;
  rationale: string;
  source: string;
  state: string;
  influencerId: string;
  structuredBrief: StructuredBrief | null;
  revision: number;
  createdAt: string;
}

type StateFilter = "all" | "proposed" | "selected" | "dismissed" | "expired";
interface CatalogProduct { id: string; title: string; status: string; }

function loadIdeas(influencerId: string, stateFilter: StateFilter): Promise<{ ideas: Idea[]; nextCursor?: string }> {
  const q = stateFilter !== "all" ? `&state=${stateFilter}` : "";
  return api.get(`/influencers/${influencerId}/ideas?limit=30${q}`);
}

export function IdeasPage() {
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [selectedInf, setSelectedInf] = useState("");
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingInf, setLoadingInf] = useState(true);
  const [stateFilter, setStateFilter] = useState<StateFilter>("proposed");

  const [manualTitle, setManualTitle] = useState("");
  const [manualRationale, setManualRationale] = useState("");
  const [showManual, setShowManual] = useState(false);
  const [formError, setFormError] = useState("");

  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [creatingShot, setCreatingShot] = useState<string | null>(null);

  const [catalogProducts, setCatalogProducts] = useState<CatalogProduct[]>([]);
  const [selectedProductId, setSelectedProductId] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    api.get<{ influencers: Influencer[] }>("/influencers?limit=50&status=active")
      .then(r => {
        setInfluencers(r.influencers ?? []);
        if (r.influencers?.length && r.influencers[0]) setSelectedInf(r.influencers[0].id);
      })
      .finally(() => setLoadingInf(false));
  }, []);

  useEffect(() => {
    if (!selectedInf) return;
    setLoading(true);
    setSelectedProductId("");
    setCatalogProducts([]);
    loadIdeas(selectedInf, stateFilter)
      .then(r => setIdeas(r.ideas ?? []))
      .catch(() => setIdeas([]))
      .finally(() => setLoading(false));
    api.get<{ products: CatalogProduct[] }>(`/influencers/${selectedInf}/catalog-products`)
      .then(r => setCatalogProducts(r.products ?? []))
      .catch(() => setCatalogProducts([]));
  }, [selectedInf, stateFilter]);

  async function refresh() {
    if (!selectedInf) return;
    loadIdeas(selectedInf, stateFilter).then(r => setIdeas(r.ideas ?? [])).catch(() => {});
  }

  async function handleManualCreate() {
    setFormError("");
    if (!manualTitle.trim()) { setFormError("El título es obligatorio"); return; }
    try {
      await api.post("/ideas", { influencerId: selectedInf, title: manualTitle.trim(), rationale: manualRationale.trim() });
      setManualTitle(""); setManualRationale(""); setShowManual(false);
      refresh();
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      setFormError(e.data?.message || "Error al crear");
    }
  }

  async function handleGenerate() {
    setGenerateError("");
    setGenerating(true);
    try {
      const body: Record<string, unknown> = { influencerId: selectedInf, count: 3 };
      if (selectedProductId) body.productId = selectedProductId;
      await api.post("/ideas/generate", body);
      refresh();
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      setGenerateError(e.data?.message || "Error al generar");
    } finally {
      setGenerating(false);
    }
  }

  async function handleCreateShot(idea: Idea) {
    setCreatingShot(idea.id);
    try {
      const result = await api.post<{ sessionId: string; shotId: string; brief: string; productAssetId: string | null; influencerRefAssetId: string | null }>(
        `/ideas/${idea.id}/create-shot`, {}
      );
      navigate(`/sessions/${result.sessionId}`, {
        state: { shotId: result.shotId, brief: result.brief, productAssetId: result.productAssetId },
      });
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      alert(e.data?.message || "No se pudo crear la toma");
    } finally {
      setCreatingShot(null);
    }
  }

  async function changeState(idea: Idea, state: string) {
    try {
      const updated = await api.patch<Idea>(
        `/ideas/${idea.id}/state`,
        { state },
        { "If-Match": String(idea.revision) },
      );
      setIdeas(prev => prev.map(i => i.id === idea.id ? { ...i, state: updated.state, revision: updated.revision } : i));
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      alert(e.data?.message || "No se pudo cambiar el estado");
    }
  }

  const stateColor = (s: string): string =>
    ({ proposed: "#2d6a4f", selected: "#1d4ed8", dismissed: "#9ca3af", expired: "#d1d5db" }[s] ?? "#6b7280");

  const stateLabel = (s: string): string =>
    ({ proposed: "Propuesta", selected: "Seleccionada", dismissed: "Descartada", expired: "Expirada" }[s] ?? s);

  if (loadingInf) return <div style={{ padding: 24, color: "#888" }}>Cargando…</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Ideas de contenido</h1>
          <p style={{ color: "#666", fontSize: 13, margin: "4px 0 0" }}>Propuestas manuales y asistidas por IA</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={handleGenerate}
            disabled={generating || !selectedInf}
            style={{ ...btnPrim, background: "#1d4ed8", opacity: generating ? 0.6 : 1 }}
          >
            {generating ? "Generando…" : "✨ Generar con IA"}
          </button>
          <button onClick={() => { setShowManual(!showManual); setFormError(""); }} style={btnPrim}>+ Idea manual</button>
        </div>
      </div>

      {generateError && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", marginBottom: 16, color: "#dc2626", fontSize: 13 }}>
          {generateError}
        </div>
      )}

      {influencers.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 10, padding: 32, textAlign: "center", color: "#888" }}>
          Sin influencers activas. <Link to="/influencers" style={{ color: "#2d6a4f" }}>Crear una primero</Link>.
        </div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
            <label style={{ fontSize: 13, color: "#444", display: "flex", alignItems: "center", gap: 8 }}>
              Influencer:
              <select value={selectedInf} onChange={e => setSelectedInf(e.target.value)} style={sel}>
                {influencers.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 13, color: "#444", display: "flex", alignItems: "center", gap: 8 }}>
              Estado:
              <select value={stateFilter} onChange={e => setStateFilter(e.target.value as StateFilter)} style={sel}>
                <option value="all">Todas</option>
                <option value="proposed">Propuestas</option>
                <option value="selected">Seleccionadas</option>
                <option value="dismissed">Descartadas</option>
                <option value="expired">Expiradas</option>
              </select>
            </label>
            {catalogProducts.length > 0 && (
              <label style={{ fontSize: 13, color: "#444", display: "flex", alignItems: "center", gap: 8 }}>
                Producto (IA):
                <select value={selectedProductId} onChange={e => setSelectedProductId(e.target.value)} style={sel}>
                  <option value="">— Sin forzar —</option>
                  {catalogProducts.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </label>
            )}
          </div>

          {showManual && (
            <div style={{ background: "#fff", padding: 20, borderRadius: 10, marginBottom: 20, display: "flex", flexDirection: "column", gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>Nueva idea manual</h3>
              {formError && <div style={{ color: "#dc2626", fontSize: 13 }}>{formError}</div>}
              <input value={manualTitle} onChange={e => setManualTitle(e.target.value)} placeholder="Título de la idea" style={inp} />
              <textarea value={manualRationale} onChange={e => setManualRationale(e.target.value)} placeholder="Justificación…" rows={3} style={{ ...inp, resize: "vertical" }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={handleManualCreate} style={btnPrim}>Guardar</button>
                <button onClick={() => setShowManual(false)} style={btnSec}>Cancelar</button>
              </div>
            </div>
          )}

          {loading ? (
            <div style={{ color: "#888" }}>Cargando ideas…</div>
          ) : ideas.length === 0 ? (
            <div style={{ background: "#fff", borderRadius: 10, padding: 32, textAlign: "center", color: "#888" }}>
              Sin ideas para esta influencer. Crea una manualmente o pulsa "✨ Generar con IA".
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {ideas.map(idea => {
                const expanded = expandedId === idea.id;
                const brief = idea.structuredBrief;
                const hasProduct = typeof brief?.callToAction === "string" && brief.callToAction.includes("http");
                return (
                  <div key={idea.id} style={{
                    background: hasProduct ? "#f0fdf4" : "#fff",
                    borderRadius: 10, padding: 18,
                    borderLeft: hasProduct ? "4px solid #2d6a4f" : "4px solid transparent",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{idea.title}</div>
                        {idea.rationale && <div style={{ fontSize: 13, color: "#555" }}>{idea.rationale}</div>}

                        {/* Brief expandible (ideas LLM) */}
                        {brief && (
                          <div style={{ marginTop: 8 }}>
                            <button
                              onClick={() => setExpandedId(expanded ? null : idea.id)}
                              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#2d6a4f", padding: 0, fontWeight: 600 }}
                            >
                              {expanded ? "▲ Ocultar brief" : "▼ Ver brief"}
                            </button>
                            {expanded && (
                              <div style={{ marginTop: 8, background: "#f8faf9", borderRadius: 6, padding: 12, fontSize: 13 }}>
                                {brief.contentType && <div style={{ marginBottom: 4 }}><strong>Tipo:</strong> {brief.contentType}</div>}
                                {brief.targetAudience && <div style={{ marginBottom: 4 }}><strong>Audiencia:</strong> {brief.targetAudience}</div>}
                                {brief.callToAction && <div style={{ marginBottom: 4 }}><strong>CTA:</strong> {brief.callToAction}</div>}
                                {Array.isArray(brief.hashtags) && brief.hashtags.length > 0 && (
                                  <div style={{ marginBottom: 4 }}>
                                    <strong>Hashtags:</strong>{" "}
                                    {brief.hashtags.map((h: string) => (
                                      <span key={h} style={{ background: "#d8f3dc", color: "#2d6a4f", borderRadius: 4, padding: "1px 6px", marginRight: 4, fontSize: 12 }}>#{h}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>
                          {idea.source === "llm" ? "IA" : "Manual"} · {new Date(idea.createdAt).toLocaleDateString("es")}
                        </div>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end", flexShrink: 0 }}>
                        <span style={{
                          fontSize: 11, padding: "2px 8px", borderRadius: 12,
                          background: stateColor(idea.state) + "22",
                          color: stateColor(idea.state), fontWeight: 600,
                        }}>
                          {stateLabel(idea.state)}
                        </span>
                        {idea.state === "proposed" && (
                          <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                            <button onClick={() => changeState(idea, "selected")} style={{ ...btnMini, background: "#d8f3dc", color: "#2d6a4f" }}>Seleccionar</button>
                            <button onClick={() => changeState(idea, "dismissed")} style={{ ...btnMini, background: "#fef2f2", color: "#dc2626" }}>Descartar</button>
                          </div>
                        )}
                        {idea.state === "selected" && (
                          <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                            <button
                              onClick={() => handleCreateShot(idea)}
                              disabled={creatingShot === idea.id}
                              style={{ ...btnMini, background: "#1d4ed8", color: "#fff", opacity: creatingShot === idea.id ? 0.6 : 1, fontSize: 12, padding: "4px 10px" }}
                            >
                              {creatingShot === idea.id ? "Creando…" : "📸 Crear toma"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const btnPrim: React.CSSProperties = { padding: "8px 16px", background: "#2d6a4f", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14 };
const btnSec: React.CSSProperties = { padding: "8px 16px", border: "1px solid #ddd", borderRadius: 6, cursor: "pointer", background: "none", fontSize: 13 };
const btnMini: React.CSSProperties = { padding: "3px 8px", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 11, fontWeight: 600 };
const inp: React.CSSProperties = { padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };
const sel: React.CSSProperties = { padding: "6px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13 };
